// Parses the product code / quantity pairs out of an OCR pass over a photo of
// a warehouse receiving sheet.
//
// The sheets print one product per row as
//   [ALP-01-3408] Lost Mary MT 15000 Puffs 5% (5CT) (Strawberry Kiwi)
// with the received amount in a right hand Quantity column as
//   40.00 Units
//
// Depending on how the photo was framed, OCR either keeps a row on one line or
// splits the two columns onto separate lines, so both shapes are handled and
// the split case is stitched back together using the line positions.

// The code is always ALP-<2 digits>-<4 digits>, so any letter shaped character
// inside those two groups is a misread digit.
const DIGIT_CONFUSIONS = {
  O: '0',
  Q: '0',
  D: '0',
  I: '1',
  L: '1',
  '|': '1',
  '!': '1',
  Z: '2',
  S: '5',
  G: '6',
  T: '7',
  B: '8',
};

const DIGITISH = '0-9OQDILZSGTB|!';
const SEPARATOR = '[-\\u2010-\\u2015~_.,\\s]{0,3}';

// ALP itself also gets misread - the L turns into a 1 or an I often enough that
// it is worth accepting.
const CODE_REGEX = new RegExp(`A[L1I|]P${SEPARATOR}([${DIGITISH}]{1,3})${SEPARATOR}([${DIGITISH}]{3,6})`, 'i');

// "40.00 Units" - the trailing word is the strong signal that this number is a
// quantity and not a page number or a handwritten note. The U of Units is
// regularly read back as a J, and the second group has to stay captured so a
// lost decimal point can be told apart from a genuinely larger number.
const UNITS_REGEX = new RegExp(`([${DIGITISH}]{1,8})([.,][${DIGITISH}]{1,2})?\\s*[UJ]\\s*N\\s*[I1L|]\\s*T\\s*S`, 'i');

// Fallback for sheets where the word Units did not survive the OCR pass at all.
const BARE_NUMBER_REGEX = /(?:^|\s)(\d{1,8})([.,]\d{1,2})?\s*$/;

// Column headings seen on Odoo and spreadsheet exports of a receiving sheet.
const QUANTITY_HEADER_REGEX = /qty|quantit|units|demand|done|receiv|deliver|counted|on\s*hand/i;
const CODE_HEADER_REGEX = /code|reference|internal|sku|barcode|variant|product/i;

/**
 * Swaps the characters OCR commonly mistakes for digits back to digits.
 * @param {string} value raw OCR fragment
 * @returns {string} fragment with only digits left
 */
const toDigits = value =>
  value
    .toUpperCase()
    .split('')
    .map(character => DIGIT_CONFUSIONS[character] || character)
    .filter(character => character >= '0' && character <= '9')
    .join('');

/**
 * Vertical midpoint of a line, used to pair a product code with the quantity
 * printed across from it.
 * @param {Object} line line with an optional bounding box
 * @returns {number} midpoint on the y axis
 */
const centerY = line => {
  if (line?.bbox && typeof line.bbox.y0 === 'number' && typeof line.bbox.y1 === 'number') {
    return (line.bbox.y0 + line.bbox.y1) / 2;
  }
  return typeof line?.index === 'number' ? line.index : 0;
};

const QuantityImportUtil = {
  /**
   * Rebuilds a product code out of an OCR line.
   * @param {string} text a single line of recognized text
   * @returns {string|null} the code as ALP-01-3408, or null when the line has none
   */
  findProductCode: text => {
    if (!text) {
      return null;
    }

    const match = text.match(CODE_REGEX);
    if (!match) {
      return null;
    }

    const group = toDigits(match[1]);
    const item = toDigits(match[2]);
    if (group.length < 1 || item.length < 3) {
      return null;
    }

    return `ALP-${group.padStart(2, '0')}-${item}`;
  },

  /**
   * The sheets always print the amount to two decimal places, so a run of
   * digits that arrived without its decimal separator is carrying the two lost
   * decimal places on the end - "4000 Units" is 40 received, not four thousand.
   * Getting this wrong inflates a quantity a hundredfold, and a plausible
   * looking number is exactly what a reviewer waves through.
   * @param {string} digits the whole digit run OCR returned
   * @param {boolean} hasSeparator whether a decimal separator survived
   * @returns {number|null} the quantity in whole units
   */
  toWholeUnits: (digits, hasSeparator) => {
    let whole = digits;
    if (!hasSeparator && whole.length >= 3 && whole.slice(-2) === '00') {
      whole = whole.slice(0, -2);
    }

    const quantity = parseInt(whole, 10);
    return Number.isNaN(quantity) ? null : quantity;
  },

  /**
   * Pulls the received amount out of an OCR line, keeping the raw digits so the
   * word that produced them can be traced back for its confidence score.
   * @param {string} text a single line of recognized text
   * @param {boolean} unitsOnly when true only "<number> Units" counts as a quantity
   * @returns {Object|null} { quantity, raw } or null when the line has none
   */
  matchQuantity: (text, unitsOnly = true) => {
    if (!text) {
      return null;
    }

    // Stray backslashes are the pen strokes staff tick each row off with.
    const cleaned = text.replace(/\\/g, '');

    const unitsMatch = cleaned.match(UNITS_REGEX);
    if (unitsMatch) {
      return { quantity: QuantityImportUtil.toWholeUnits(toDigits(unitsMatch[1]), Boolean(unitsMatch[2])), raw: unitsMatch[1] };
    }

    if (unitsOnly) {
      return null;
    }

    const bareMatch = cleaned.match(BARE_NUMBER_REGEX);
    if (bareMatch) {
      return { quantity: QuantityImportUtil.toWholeUnits(bareMatch[1], Boolean(bareMatch[2])), raw: bareMatch[1] };
    }

    return null;
  },

  /**
   * Pulls the received amount out of an OCR line.
   * @param {string} text a single line of recognized text
   * @param {boolean} unitsOnly when true only "<number> Units" counts as a quantity
   * @returns {number|null} the quantity, or null when the line has none
   */
  findQuantity: (text, unitsOnly = true) => {
    const match = QuantityImportUtil.matchQuantity(text, unitsOnly);
    return match ? match.quantity : null;
  },

  /**
   * Confidence of the individual word a quantity was read out of. A misread
   * digit tanks this score long before it shows in the line average, which is
   * what makes a wrong quantity - the costliest mistake here - catchable.
   * @param {Object} line the recognized line
   * @param {string} raw the digit run the quantity was parsed from
   * @returns {number|null} the word confidence, or null when it cannot be traced
   */
  quantityWordConfidence: (line, raw) => {
    if (!raw || !Array.isArray(line?.words)) {
      return null;
    }

    // Searched right to left because the quantity column sits on the right, and
    // product codes are skipped because a code like ALP-01-3408 contains the
    // digits of a quantity like 40 and would otherwise be scored instead.
    for (let index = line.words.length - 1; index >= 0; index--) {
      const text = (line.words[index]?.text || '').replace(/\\/g, '');
      if (text.includes(raw) && QuantityImportUtil.findProductCode(text) === null) {
        return typeof line.words[index].confidence === 'number' ? line.words[index].confidence : null;
      }
    }

    return null;
  },

  /**
   * Turns recognized lines into the rows shown in the review table.
   * @param {Array} lines objects of { text, bbox, confidence, words } as returned by the OCR pass
   * @returns {Array} rows of { product_code, quantity, confidence, quantity_confidence }
   */
  parseLines: lines => {
    if (!Array.isArray(lines) || lines.length === 0) {
      return [];
    }

    const indexed = lines.map((line, index) => ({
      text: typeof line === 'string' ? line : line?.text || '',
      bbox: typeof line === 'string' ? null : line?.bbox || null,
      confidence: typeof line === 'string' || typeof line?.confidence !== 'number' ? null : line.confidence,
      words: typeof line === 'string' ? null : line?.words || null,
      index,
    }));

    // Prefer the lines that spelled out Units. Only when the whole sheet came
    // back without a single one do bare trailing numbers get considered, since
    // page numbers and handwritten margin notes look just like them.
    const hasUnits = indexed.some(line => QuantityImportUtil.findQuantity(line.text, true) !== null);

    const paired = [];
    const codeOnly = [];
    const quantityOnly = [];

    indexed.forEach(line => {
      const productCode = QuantityImportUtil.findProductCode(line.text);
      const match = QuantityImportUtil.matchQuantity(line.text, hasUnits);
      const quantity = match ? match.quantity : null;
      const quantityConfidence = match ? QuantityImportUtil.quantityWordConfidence(line, match.raw) : null;

      if (productCode && quantity !== null) {
        paired.push({ product_code: productCode, quantity, confidence: line.confidence, quantity_confidence: quantityConfidence, sort: centerY(line) });
      } else if (productCode) {
        codeOnly.push({ product_code: productCode, sort: centerY(line), line });
      } else if (quantity !== null) {
        quantityOnly.push({ quantity, quantity_confidence: quantityConfidence, sort: centerY(line), line });
      }
    });

    const rows = paired.concat(QuantityImportUtil.pairColumns(codeOnly, quantityOnly));

    return rows.sort((a, b) => a.sort - b.sort).map(row => ({
      product_code: row.product_code,
      quantity: row.quantity,
      confidence: row.confidence === undefined ? null : row.confidence,
      quantity_confidence: row.quantity_confidence === undefined ? null : row.quantity_confidence,
    }));
  },

  /**
   * Matches product codes to the quantities printed in the neighbouring column
   * when OCR split the two into separate lines.
   * @param {Array} codeOnly lines that only produced a product code
   * @param {Array} quantityOnly lines that only produced a quantity
   * @returns {Array} rows of { product_code, quantity, sort }
   */
  pairColumns: (codeOnly, quantityOnly) => {
    const codes = codeOnly.slice().sort((a, b) => a.sort - b.sort);
    const quantities = quantityOnly.slice().sort((a, b) => a.sort - b.sort);

    // A photo taken at an angle shifts the quantity column up or down relative
    // to the product column, so when both columns produced the same number of
    // entries their reading order is the trustworthy pairing.
    // A split row is only as trustworthy as its weaker half.
    const combine = (code, quantity) => ({
      product_code: code.product_code,
      quantity: quantity.quantity,
      confidence: QuantityImportUtil.lowest(code.line?.confidence, quantity.line?.confidence),
      quantity_confidence: quantity.quantity_confidence === undefined ? null : quantity.quantity_confidence,
      sort: code.sort,
    });

    if (codes.length > 0 && codes.length === quantities.length) {
      return codes.map((code, index) => combine(code, quantities[index]));
    }

    // Otherwise fall back to whichever quantity sits closest to the code, and
    // leave the quantity blank when nothing lines up so staff can fill it in.
    const claimed = [];
    return codes.map(code => {
      let bestIndex = -1;
      let bestDistance = Number.MAX_VALUE;

      quantities.forEach((quantity, index) => {
        if (claimed.includes(index)) {
          return;
        }
        const distance = Math.abs(quantity.sort - code.sort);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      if (bestIndex === -1) {
        return { product_code: code.product_code, quantity: '', confidence: code.line?.confidence ?? null, quantity_confidence: null, sort: code.sort };
      }

      claimed.push(bestIndex);
      return combine(code, quantities[bestIndex]);
    });
  },

  /**
   * Lowest of the supplied confidences, ignoring the ones that are unknown.
   * @param {...(number|null|undefined)} values confidence scores
   * @returns {number|null} the weakest known score
   */
  lowest: (...values) => {
    const known = values.filter(value => typeof value === 'number');
    return known.length === 0 ? null : Math.min(...known);
  },

  /**
   * Reads a quantity out of a spreadsheet cell.
   *
   * Deliberately separate from the OCR path: a CSV value is exact, so the
   * decimal places are really there and the "digits ending in 00 lost their
   * decimal point" recovery must not run. Applying it here would turn a
   * genuine 100 into 1.
   * @param {string} value the raw cell contents
   * @returns {number|null} the quantity in whole units
   */
  parseCsvQuantity: value => {
    if (value === null || value === undefined) {
      return null;
    }

    // A removal can be written as -5, 5-, (5) in accounting format, or with
    // the Unicode minus or a dash some spreadsheets and autocorrect swap in.
    // Missing any of them would flip a removal into an addition, so the sign
    // is read off the raw text before everything but the number is stripped.
    const text = String(value).trim();
    const negative = /^\(.*\)$/.test(text) || /[-‒-―−]/.test(text);

    const stripped = text.replace(/[^0-9.,]/g, '');
    if (stripped === '' || !/\d/.test(stripped)) {
      return null;
    }

    const dots = (stripped.match(/\./g) || []).length;
    const commas = (stripped.match(/,/g) || []).length;
    let normalized;

    if (dots > 0 && commas > 0) {
      // Whichever separator comes last is the decimal one, the other groups
      // thousands.
      const decimalAt = Math.max(stripped.lastIndexOf('.'), stripped.lastIndexOf(','));
      normalized = stripped.slice(0, decimalAt).replace(/[.,]/g, '') + '.' + stripped.slice(decimalAt + 1).replace(/[.,]/g, '');
    } else if (dots === 1) {
      // A lone dot is the decimal point. Odoo writes quantities to three
      // decimals, so 40.000 is forty - reading it as grouping would multiply
      // every quantity on the sheet by a thousand.
      normalized = stripped;
    } else if (commas === 1 && stripped.length - stripped.indexOf(',') - 1 <= 2) {
      // A lone comma with one or two digits behind it is a European decimal
      // comma. With three behind it, it is grouping.
      normalized = stripped.replace(',', '.');
    } else {
      normalized = stripped.replace(/[.,]/g, '');
    }

    // Rounded before the sign goes on so -12.5 and 12.5 round the same distance.
    const quantity = Math.round(parseFloat(normalized));
    if (Number.isNaN(quantity)) {
      return null;
    }
    return negative && quantity !== 0 ? -quantity : quantity;
  },

  /**
   * Locates the column holding the product codes - the one where the most cells
   * parse as a code, which survives an export with no headers at all.
   * @param {Array} grid rows of cells
   * @returns {number} the column index, or -1 when no column carries codes
   */
  findCodeColumn: grid => {
    const width = grid.reduce((widest, row) => Math.max(widest, row.length), 0);
    let column = -1;
    let best = 0;

    for (let index = 0; index < width; index++) {
      const hits = grid.filter(row => QuantityImportUtil.findProductCode(row[index]) !== null).length;
      if (hits > best) {
        best = hits;
        column = index;
      }
    }

    return column;
  },

  /**
   * Locates the column holding the received amounts. A matching heading wins,
   * because guessing by content alone can just as easily land on a unit price
   * or the puff count baked into a product name.
   * @param {Array} grid rows of cells
   * @param {Array} dataRows the rows carrying product codes
   * @param {number} codeColumn the column already claimed by the codes
   * @returns {number} the column index, or -1 when nothing looks like a quantity
   */
  findQuantityColumn: (grid, dataRows, codeColumn) => {
    const width = grid.reduce((widest, row) => Math.max(widest, row.length), 0);

    // A header row is one that sits above the data and carries no product code.
    const header = grid.find(row => QuantityImportUtil.findCodeColumn([row]) === -1 && row.some(cell => (cell || '').trim() !== ''));
    if (header) {
      for (let index = 0; index < width; index++) {
        if (index === codeColumn) {
          continue;
        }
        const heading = (header[index] || '').trim();
        if (heading !== '' && QUANTITY_HEADER_REGEX.test(heading) && !CODE_HEADER_REGEX.test(heading)) {
          return index;
        }
      }
    }

    // No usable heading, so fall back to the column that most consistently
    // holds a plain non-zero number.
    let column = -1;
    let best = 0;
    for (let index = 0; index < width; index++) {
      if (index === codeColumn) {
        continue;
      }
      const hits = dataRows.filter(row => {
        const quantity = QuantityImportUtil.parseCsvQuantity(row[index]);
        return quantity !== null && quantity !== 0;
      }).length;
      if (hits > best) {
        best = hits;
        column = index;
      }
    }

    return column;
  },

  /**
   * Turns a parsed CSV grid into the rows shown in the review table.
   * @param {Array} grid rows of cells
   * @returns {Array} rows of { product_code, quantity, confidence, quantity_confidence }
   */
  parseCsvRows: grid => {
    if (!Array.isArray(grid) || grid.length === 0) {
      return [];
    }

    const codeColumn = QuantityImportUtil.findCodeColumn(grid);
    if (codeColumn === -1) {
      return [];
    }

    const dataRows = grid.filter(row => QuantityImportUtil.findProductCode(row[codeColumn]) !== null);
    const quantityColumn = QuantityImportUtil.findQuantityColumn(grid, dataRows, codeColumn);

    return dataRows.map(row => {
      const quantity = quantityColumn === -1 ? null : QuantityImportUtil.parseCsvQuantity(row[quantityColumn]);
      return {
        product_code: QuantityImportUtil.findProductCode(row[codeColumn]),
        quantity: quantity === null || quantity === 0 ? '' : quantity,
        // A spreadsheet value is exact, so there is nothing to be unsure about
        // and nothing to flag for a second look.
        confidence: null,
        quantity_confidence: null,
      };
    });
  },

  /**
   * Flattens the block/paragraph tree an OCR pass returns down to lines.
   * @param {Array} blocks recognized blocks
   * @returns {Array} lines of { text, bbox }
   */
  flattenBlocks: blocks => {
    if (!Array.isArray(blocks)) {
      return [];
    }

    return blocks.reduce((lines, block) => {
      (block?.paragraphs || []).forEach(paragraph => {
        (paragraph?.lines || []).forEach(line => lines.push(line));
      });
      return lines;
    }, []);
  },

  /**
   * Entry point for a finished OCR pass. Line boxes give the best results, but
   * a plain text result still parses - it just cannot stitch split columns back
   * together as reliably.
   * @param {Object} data the recognize() result, which may or may not carry line boxes
   * @returns {Array} rows of { product_code, quantity }
   */
  parseOcrResult: data => {
    if (Array.isArray(data?.lines) && data.lines.length > 0) {
      return QuantityImportUtil.parseLines(data.lines);
    }

    const blockLines = QuantityImportUtil.flattenBlocks(data?.blocks);
    if (blockLines.length > 0) {
      return QuantityImportUtil.parseLines(blockLines);
    }

    return QuantityImportUtil.parseLines((data?.text || '').split(/\r?\n/).filter(line => line.trim() !== ''));
  },
};

export default QuantityImportUtil;
